// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PanelMenuPresentationProps } from "@synd/MobiusOverrides/PanelMenu";
import { PopoverStyle } from "@synd/MobiusOverrides/Popover";
import AngleDown from "@synd/XDL/Icons/AngleDown";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { styledScrollbar, xScreen } from "@synd/XDL/Utilities";
import color, { getSystemColor } from "@synd/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@synd/XDL/Utilities/links";
import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";
import { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui4",
        },
        text: {
            default: "common.white",
            hover: "common.white",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 5px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 13px;
                padding: 10px 15px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

const linkSizeVariants = {
    regular: {
        size: 16,
        lineHeight: "22px",
        iconSize: 16,
    },
    small: {
        size: 14,
        lineHeight: "20px",
        iconSize: 16,
    },
    xsmall: {
        size: 12,
        lineHeight: "17px",
        iconSize: 12,
    },
    xxsmall: {
        size: 10,
        lineHeight: "14px",
        iconSize: 10,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "regular",
    variant = "link",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "regular" | "small" | "xsmall" | "xxsmall";
    variant?: "primary" | "link";
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    const color = variant === "primary" ? "primary" : "text.link";
    const hoverColor = variant === "primary" ? "common.ui12" : "common.ui10";
    return {
        ...mergeWithCss(
            {
                color,
                hoverStyle: {
                    color: getSystemColor(hoverColor),
                },
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: linkSizeVariants[sizeVariant].iconSize,
                        css: css`
                            ${marginPosition}: 6px;
                        `,
                    },
                    position: linkStyles?.icon?.position,
                },
                css: css`
                    ${linkStyles?.icon?.position === "right"
                        ? css`
                              width: fit-content;
                          `
                        : ""}
                    &[disabled] {
                        cursor: not-allowed;
                        & ${TypographyStyle} {
                            color: ${getSystemColor(color, 0.33)};
                        }
                        & ${IconWrapper} {
                            opacity: 0.33;
                            color: ${getSystemColor(color)};
                        }
                        &:hover {
                            ${IconWrapper} {
                                opacity: 0.33;
                                color: ${getSystemColor(color)};
                            }
                        }
                    }
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(
                iconSrc,
                12,
                {
                    typographyProps: {
                        size: fontConfig.xSmallSingleLine.size,
                        lineHeight: fontConfig.xSmallSingleLine.lineHeight,
                        weight: fontConfig.weight.medium,
                        color: "primary",
                    },
                },
                "primary",
            ),
            {
                css: css`
                    margin: 0 6px;
                    display: flex;
                `,
            },
        ),
    };
};

export const sharedBadgeStyle: ProductTextBadgesStyles = {
    badge: {
        css: css`
            margin-top: 8px;
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
            & > div {
                border-radius: 8px;
                display: flex;
                flex-shrink: 0;
                gap: 4px;
                padding: 6px;
                align-items: center;
                justify-content: center;
            }
        `,
    },
    badgeText: {
        ...fontConfig.xxSmallSingleLine,
        weight: fontConfig.weight.semiBold,
    },
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            width: 100%;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        variant: "primary",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const dotButtonStyles: ButtonPresentationProps = {
    css: css`
        height: 10px;
        width: 10px;
        min-height: unset;
        padding: 0;
        margin-right: 4px;
        border: 1px solid ${color("common.ui1")};
        border-radius: 5px;
        background: transparent;

        &:active,
        &:focus {
            &&&& {
                width: 20px;
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }
        &:hover {
            &&&& {
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }

        &.slide-dot-button-selected {
            width: 20px;
            background: ${color("common.ui1")};
        }
    `,
};

export const selectedPanelItem = css`
    cursor: pointer;
    background: ${getSystemColor("common.ui8")};
    & ${IconWrapper} {
        color: ${getSystemColor("primary")};
    }
`;
export const sharedPanelMenuStyles: PanelMenuPresentationProps = {
    mergeCss: true,
    backIconProps: {
        src: ChevronLeft,
        color: "text.link",
        css: css`
            opacity: 0.66;
            margin-right: 0;
            &,
            & svg {
                width: 13px;
                height: 13px;
            }
        `,
    },
    panelChildrenSeparator: {
        css: css`
            margin: 16px auto 10px;
            border-top: 1px solid ${color("common.border")};
            width: calc(100% - 32px);
        `,
    },
    cssOverrides: {
        children: css`
            background-color: ${color("common.white")};
            & > button {
                padding: 0px;
                font-size: 14px;
                line-height: 22px;
                font-weight: 400;
                min-height: 22px;
                color: ${color("common.black")};
                ${IconWrapper} {
                    color: ${color("text.link")};
                }
            }
        `,
        wrapper: css`
            & > ${IconWrapper} {
                padding-right: 16px;
                width: 24px;
            }

            &:hover:not(:disabled),
            &:hover:not(:disabled) > ${StyledButton}, &:focus:not(:disabled),
            &:focus:not(:disabled) > ${StyledButton} {
                // if it has selected item then consider it as selected
                ${selectedPanelItem}
            }
        `,
        menu: css`
            display: flex;
            flex-direction: column;
            row-gap: 4px;
            ${styledScrollbar()}
        `,
    },
    panelRowProps: {
        css: css`
            padding: 6px 16px;
            border-radius: 0;
            margin: 0;
            text-align: left;
            ${IconWrapper} {
                color: ${color("text.link")};
                width: auto;
                height: 100%;
            }
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 22px;
            }
        `,
        isCurrentCss: css`
            ${selectedPanelItem}
        `,
    },
};

export const emptyValueStyles = {
    css: css`
        font-size: 30px;
        color: ${color("common.ui5")};
    `,
};

export const StyledHr = getStyledWrapper("hr");

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}
export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }

                ${PopoverStyle} {
                    box-shadow: 0 1px 2px 0 ${color("common.ui7", 0.2)};
                    border: 1px solid ${color("common.border")};
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,

            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 18px;
                font-weight: 400;
                color: ${color("text")};
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        color: "text",
        icon: {
            iconProps: {
                src: AngleDown,
                size: 11,
            },
            position: "right",
        },
        typographyProps: {
            size: 12,
            lineHeight: "22px",
            weight: 600,
        },
    },
};

export const variantSelectionLabel: TypographyPresentationProps = {
    ...fontConfig.smallSingleLine,
    transform: "uppercase",
    weight: fontConfig.weight.semiBold,
    color: "common.ui6",
};
